import axios from "axios";
import { IEstablishment } from "./establishment";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// The dashboard deliberately fetches one endpoint per block instead of a single /stats
// call: each block renders as soon as its own request lands, and each one is cached
// server-side (Redis, 12 h) on its own key.

export interface WeekData {
    weekKey: string;
    weekLabel: string;
    restaurant_opening: number;
    restaurant_owner_change: number;
    hygiene_control: number;
    other: number;
    total: number;
}

export interface WeekStats {
    total: number;
    restaurant_opening: number;
    restaurant_owner_change: number;
    hygiene_control: number;
    other: number;
}

export const getDashboardWeek = async (): Promise<WeekStats> => {
    const response = await axios.get(`${API_URL}/dashboard/stats/week`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const getDashboardMonth = async (): Promise<{ total: number }> => {
    const response = await axios.get(`${API_URL}/dashboard/stats/month`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const getDashboardTotal = async (): Promise<{ totalInBase: number }> => {
    const response = await axios.get(`${API_URL}/dashboard/stats/total`, {
        headers: authHeaders(),
    });
    return response.data;
};

// The same span as `byWeek`, collapsed on the NAF axis: which trade the detections come
// from. `code` is "56.10A" (or "other" for the folded tail), and every signal-type field
// is a subset of `total`.
export interface NafCounts {
    total: number;
    restaurant_opening: number;
    restaurant_owner_change: number;
    hygiene_control: number;
    other: number;
}

export interface NafBreakdownRow extends NafCounts {
    code: string;
    label: string;
    // Same counts, split into the window's two halves — lets the UI derive a trend for
    // whichever metric is selected, and re-fold it correctly when rows get grouped into
    // "Autres" past the visible cutoff (summing counts is valid, averaging percentages isn't).
    recent: NafCounts;
    previous: NafCounts;
}

// `byWeek` covers the last CHART_WEEKS ISO weeks (see ModernDashboard) — the API and the
// chart agree on the span, so every bar drawn is a week the API actually counted.
export const getDashboardWeekly = async (): Promise<{
    byWeek: WeekData[];
    naf: NafBreakdownRow[];
}> => {
    const response = await axios.get(`${API_URL}/dashboard/stats/weekly`, {
        headers: authHeaders(),
    });
    return response.data;
};

export interface MonthData {
    monthKey: string;
    monthLabel: string;
    restaurant_opening: number;
    restaurant_owner_change: number;
    hygiene_control: number;
    other: number;
    total: number;
}

// Same shape as `byWeek`, bucketed by calendar month over the last CHART_MONTHS months
// instead — the Marché tab's week/month toggle switches which of the two it draws from.
export const getDashboardMonthly = async (): Promise<{ byMonth: MonthData[] }> => {
    const response = await axios.get(`${API_URL}/dashboard/stats/monthly`, {
        headers: authHeaders(),
    });
    return response.data;
};

// Five profiles to add on LinkedIn, rotated daily and per user: multi-site operators
// first, then the best-known single operators (Google reviews) when there aren't enough.
// Adding one removes him from that user's recommendations for good (stored on the user).
export interface LinkedinRecommendation {
    personId: string;
    name: string;
    linkedinUrl: string;
    headline?: string;
    establishmentCount: number;
    googleReviews?: number;
    city: string | null;
    otherCityCount: number;
    concept: string | null;
    brands: string[];
    lastSignal: { type: string; date: string } | null;
    why: string;
}

export const getLinkedinRecommendations = async (): Promise<{ people: LinkedinRecommendation[] }> => {
    const response = await axios.get(`${API_URL}/dashboard/linkedin-recommendations`, {
        headers: authHeaders(),
    });
    return response.data;
};

export const markLinkedinPersonAdded = async (personId: string): Promise<void> => {
    await axios.post(
        `${API_URL}/dashboard/linkedin-recommendations/${personId}/added`,
        {},
        { headers: authHeaders() }
    );
};

export const getDashboardSignals = async (
    type: string,
    options?: { limit?: number; active?: boolean }
): Promise<{ establishments: IEstablishment[] }> => {
    const response = await axios.get(`${API_URL}/dashboard/signals/${type}`, {
        headers: authHeaders(),
        params: {
            limit: options?.limit ?? 3,
            ...(options?.active === false ? { active: "false" } : {}),
        },
    });
    return response.data;
};
